/**
 * Invitations Repository
 *
 * Wraps the organization-invitations API: revoking a pending invitation and
 * checking whether an email can be invited, plus the public (unauthenticated)
 * flow an invited person uses to resolve and accept an invitation token.
 *
 * Every thunk here is Promise-based and holds no Redux state of its own: no
 * reducer registers a case for an invitation action, so each caller reads the
 * resolved value or the rejection rather than selecting invitation state from
 * the store. `dispatch` is still threaded into `get`/`post`/`del` because
 * apiService uses it to sign a user out on a 401.
 */

import { getApiUrl } from 'data/services/apiConstants';
import { get, post, del } from 'data/services/apiService';

/**
 * Revoke a pending invitation.
 * Resolves with the revoked invitation payload.
 * Usage: dispatch(revokeInvitation(organizationId, invitationId))
 */
export const revokeInvitation =
	(organizationId, invitationId) => (dispatch, getState) => {
		const state = getState();
		const url = `${getApiUrl()}/api/organizations/${organizationId}/invitations/${invitationId}`;

		return new Promise((resolve, reject) => {
			del({
				dispatch,
				url,
				authToken: state.user.authToken,
				onSuccess: (result) => {
					resolve(result?.data ?? result);
				},
				onError: (error) => {
					reject(error);
				},
			}).catch(() => {
				// onError above already settled this thunk's own Promise; del()
				// rethrows after calling onError, so this swallows that second,
				// otherwise-unhandled rejection rather than letting it surface.
			});
		});
	};

/**
 * Check whether an email can be invited to an organization.
 * Resolves with the eligibility payload (`canBeInvited`, `reason`).
 * Usage: dispatch(checkInvitationEligibility(organizationId, email))
 */
export const checkInvitationEligibility =
	(organizationId, email) => (dispatch, getState) => {
		const state = getState();
		const url = `${getApiUrl()}/api/organizations/${organizationId}/invitations/eligibility?email=${encodeURIComponent(email)}`;

		return new Promise((resolve, reject) => {
			get({
				dispatch,
				url,
				authToken: state.user.authToken,
				onSuccess: (result) => {
					resolve(result?.data ?? result);
				},
				onError: (error) => {
					reject(error);
				},
			}).catch(() => {
				// See revokeInvitation: get() rethrows after onError runs.
			});
		});
	};

/**
 * Resolve a public invitation token to its display details.
 * PUBLIC endpoint -- deliberately does not read or send an authToken, since
 * an invited person is not signed in yet when they open the invite link.
 * Usage: dispatch(resolveInvitation(token))
 */
export const resolveInvitation = (token) => (dispatch) => {
	const url = `${getApiUrl()}/api/invitations/${token}`;

	return new Promise((resolve, reject) => {
		get({
			dispatch,
			url,
			onSuccess: (result) => {
				resolve(result?.data ?? result);
			},
			onError: (error) => {
				reject(error);
			},
		}).catch(() => {
			// See revokeInvitation: get() rethrows after onError runs.
		});
	});
};

/**
 * Accept an invitation token.
 * Unlike `resolveInvitation` (GET, public), this endpoint sits inside the
 * jwt.auth middleware group and the controller calls getUser() unconditionally
 * -- it is NOT public and always requires a signed-in user. A logged-out
 * invitee must complete sign-up or sign-in first (obtaining an authToken)
 * before this thunk can succeed; calling it without one 401s, which trips
 * apiService's sessionExpiredHandler.
 *
 * The backend (OrganizationInvitationController::accept()) does not return a
 * literal `outcome` string -- the caller derives it: a 200 with
 * `requiresConfirmation: true` in the data means REQUIRES_CONFIRMATION (with
 * `propertyCount` and `organizationName` alongside it); a plain 200 means
 * JOINED; a rejected promise (409 conflict, or the 403 mismatched-email case)
 * means BLOCKED, with `error.message` carrying the reason verbatim.
 *
 * `confirmed` re-submits the accept after a REQUIRES_CONFIRMATION response,
 * per `Request::boolean('confirmed')` on the backend.
 * Usage: dispatch(acceptInvitation(token, { confirmed: true }))
 */
export const acceptInvitation =
	(token, { confirmed = false } = {}) =>
	(dispatch, getState) => {
		const state = getState();
		const url = `${getApiUrl()}/api/invitations/${token}/accept`;

		return new Promise((resolve, reject) => {
			post({
				dispatch,
				url,
				authToken: state.user?.authToken,
				data: { confirmed },
				onSuccess: (result) => {
					resolve(result?.data ?? result);
				},
				onError: (error) => {
					reject(error);
				},
			}).catch(() => {
				// See revokeInvitation: post() rethrows after onError runs.
			});
		});
	};
